import React, { useState, useEffect } from 'react';
import {
  Button,
  ConfirmationDialog,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DatePicker,
  TimePicker,
  normalizeTimeValue,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  CollapsibleSection,
} from '@roger/ui-components';
import {
  createInfectionLog,
  getInfectionLogDetail,
  updateInfectionLog,
  deleteInfectionLog,
} from '../../services/api';
import type {
  AvailableClinician,
  ClinicPhysicianResponse,
  InfectionCovidScreeningCreate,
  InfectionLogDetailResponse,
  YesNoNa,
} from '../../services/api';
import { useToast } from '../../hooks/useToastContext';
import { useEpisodeDateBounds } from '../../hooks/useEpisodeDateBounds';
import { useLogDelete } from '../../hooks/useLogDelete';
import { parse4xxHeyApiResult } from '../../utils/apiErrorDetail';
import { formatDateToISO, parseISOToLocalDate } from '../../utils/dateUtils';
import { getClinicianDisplayName } from '../../utils/clinicianUtils';
import { COVID_SYMPTOMS, INFECTION_TYPE_OPTIONS } from './infectionConstants';

const toISO = (d: Date | null | undefined): string | null =>
  d ? formatDateToISO(d) : null;

interface AddInfectionLogDialogProps {
  open: boolean;
  onClose: () => void;
  patientId: string;
  episodeId?: string | null;
  episodeStartDate?: string | null;
  episodeEndDate?: string | null;
  clinicians: AvailableClinician[];
  physicians: ClinicPhysicianResponse[];
  onCreated: () => void;
  /** If set, load this log and edit it instead of creating new */
  editingLogId?: string | null;
  /** Already-fetched log data — skips the GET request if provided (avoids double-fetch from detail dialog) */
  editingLogData?: InfectionLogDetailResponse | null;
}

const FieldLabel: React.FC<{
  children: React.ReactNode;
  required?: boolean;
}> = ({ children, required }) => (
  <p className='text-sm font-medium mb-1.5'>
    {children}
    {required && <span className='text-destructive ml-0.5'>*</span>}
  </p>
);

const YesNoNaGroup: React.FC<{
  label: string;
  name: string;
  value: YesNoNa | '';
  onChange: (v: YesNoNa) => void;
}> = ({ label, name, value, onChange }) => (
  <div className='flex items-center gap-6'>
    <span className='text-sm font-medium w-48 shrink-0'>{label}</span>
    <div className='flex gap-4'>
      {(['yes', 'no', 'na'] as const).map(opt => (
        <label
          key={opt}
          className='flex items-center gap-1.5 cursor-pointer text-sm'
        >
          <input
            type='radio'
            name={name}
            value={opt}
            checked={value === opt}
            onChange={() => onChange(opt)}
            className='accent-primary'
          />
          {opt === 'na' ? 'N/A' : opt.charAt(0).toUpperCase() + opt.slice(1)}
        </label>
      ))}
    </div>
  </div>
);

// COVID_SYMPTOMS imported from infectionConstants

// INFECTION_TYPES → use INFECTION_TYPE_OPTIONS from infectionConstants

const INFECTION_SITES = [
  { value: 'abdominal', label: 'Abdominal' },
  { value: 'catheter', label: 'Catheter' },
  { value: 'cervix', label: 'Cervix' },
  { value: 'cns', label: 'CNS' },
  { value: 'ear', label: 'Ear' },
  { value: 'eye', label: 'Eye' },
  { value: 'foot', label: 'Foot' },
  { value: 'iv_site', label: 'IV Site' },
  { value: 'mouth', label: 'Mouth' },
  { value: 'urinary_tract', label: 'Urinary Tract' },
  { value: 'respiratory', label: 'Respiratory' },
  { value: 'surgical_wound', label: 'Surgical Wound' },
  { value: 'throat', label: 'Throat' },
  { value: 'other', label: 'Other' },
];

const DISCOVERY_LOCATIONS = [
  { value: 'alf', label: 'ALF' },
  { value: 'hospital', label: 'Hospital' },
  { value: 'patient_home', label: 'Patient Home' },
  { value: 'psychiatric_facility', label: 'Psychiatric Facility' },
  { value: 'snf', label: 'SNF' },
  { value: 'other', label: 'Other' },
];

function getPhysicianLabel(p: ClinicPhysicianResponse): string {
  return [p.first_name, p.last_name, p.suffix].filter(Boolean).join(' ');
}

export const AddInfectionLogDialog: React.FC<AddInfectionLogDialogProps> = ({
  open,
  onClose,
  patientId,
  episodeId,
  episodeStartDate,
  episodeEndDate,
  clinicians,
  physicians,
  onCreated,
  editingLogId,
  editingLogData,
}) => {
  const { success: showSuccess, error: showError } = useToast();
  const [submitting, setSubmitting] = useState(false);
  const isEditing = Boolean(editingLogId);
  const { deleting, confirmDeleteOpen, setConfirmDeleteOpen, handleDelete } =
    useLogDelete({
      entityLabel: 'infection log',
      performDelete: () =>
        deleteInfectionLog({
          path: { patient_id: patientId, infection_log_id: editingLogId! },
        }),
      onDeleted: () => {
        resetForm();
        onCreated();
      },
    });
  // Date Reported must be within the episode and not in the future
  const {
    minDate: minReportedDate,
    maxDate: maxReportedDate,
    today,
  } = useEpisodeDateBounds(open, episodeStartDate, episodeEndDate);

  // Patient Information
  const [primaryPhysicianId, setPrimaryPhysicianId] = useState('');
  const [caseManagerId, setCaseManagerId] = useState(''); // user_clinic_id
  const [reportedByUserId, setReportedByUserId] = useState(''); // user_id
  const [dateReported, setDateReported] = useState<Date | undefined>(undefined);
  const [timeReported, setTimeReported] = useState('');
  const [reportedToUserId, setReportedToUserId] = useState(''); // user_id

  // Infection Information
  const [infectionStatus, setInfectionStatus] = useState('investigating');
  const [dateOfOnset, setDateOfOnset] = useState<Date | undefined>(undefined);
  const [presentUponAdmission, setPresentUponAdmission] = useState(false);
  const [infectionType, setInfectionType] = useState('');
  const [infectionTypeOther, setInfectionTypeOther] = useState('');
  const [infectionSite, setInfectionSite] = useState('');
  const [infectionSiteOther, setInfectionSiteOther] = useState('');
  const [discoveryLocation, setDiscoveryLocation] = useState('');
  const [discoveryLocationOther, setDiscoveryLocationOther] = useState('');
  const [isCommunicable, setIsCommunicable] = useState('');
  const [infectionDescription, setInfectionDescription] = useState('');
  const [signsSymptomsText, setSignsSymptomsText] = useState('');

  // COVID-19 symptoms
  const [covidSymptoms, setCovidSymptoms] = useState<
    Record<string, boolean | null>
  >({});
  const [covidContact, setCovidContact] = useState('');

  // Actions
  const [physicianNotified, setPhysicianNotified] = useState<YesNoNa | ''>('');
  const [physicianNotifiedPhysicianId, setPhysicianNotifiedPhysicianId] =
    useState('');
  const [physicianNotifiedDate, setPhysicianNotifiedDate] = useState<
    Date | undefined
  >(undefined);
  const [physicianNotifiedTime, setPhysicianNotifiedTime] = useState('');
  const [labsOrdered, setLabsOrdered] = useState<YesNoNa | ''>('');
  const [labsOrderedDate, setLabsOrderedDate] = useState<Date | undefined>(
    undefined
  );
  const [labsOrderedTime, setLabsOrderedTime] = useState('');
  const [labsOrderedNotes, setLabsOrderedNotes] = useState('');
  const [patientHospitalized, setPatientHospitalized] = useState<YesNoNa | ''>(
    ''
  );
  const [patientHospitalizedDate, setPatientHospitalizedDate] = useState<
    Date | undefined
  >(undefined);
  const [patientHospitalizedTime, setPatientHospitalizedTime] = useState('');
  const [patientHospitalizedNotes, setPatientHospitalizedNotes] = useState('');
  const [cultureTaken, setCultureTaken] = useState<YesNoNa | ''>('');
  const [cultureTakenDate, setCultureTakenDate] = useState<Date | undefined>(
    undefined
  );
  const [cultureTakenTime, setCultureTakenTime] = useState('');
  const [cultureSource, setCultureSource] = useState('');
  const [treatmentNotes, setTreatmentNotes] = useState('');

  // Follow-Up
  const [followUpDueDate, setFollowUpDueDate] = useState<Date | undefined>(
    undefined
  );
  const [followUpAssigneeId, setFollowUpAssigneeId] = useState(''); // user_clinic_id
  const [followUpNotes, setFollowUpNotes] = useState('');

  const isCovid = infectionType === 'covid_19';
  const isFormValid = !!dateReported && !!infectionType;

  const resetForm = () => {
    setPrimaryPhysicianId('');
    setCaseManagerId('');
    setReportedByUserId('');
    setDateReported(undefined);
    setTimeReported('');
    setReportedToUserId('');
    setInfectionStatus('investigating');
    setDateOfOnset(undefined);
    setPresentUponAdmission(false);
    setInfectionType('');
    setInfectionTypeOther('');
    setInfectionSite('');
    setInfectionSiteOther('');
    setDiscoveryLocation('');
    setDiscoveryLocationOther('');
    setIsCommunicable('');
    setInfectionDescription('');
    setSignsSymptomsText('');
    setCovidSymptoms({});
    setCovidContact('');
    setPhysicianNotified('');
    setPhysicianNotifiedPhysicianId('');
    setPhysicianNotifiedDate(undefined);
    setPhysicianNotifiedTime('');
    setLabsOrdered('');
    setLabsOrderedDate(undefined);
    setLabsOrderedTime('');
    setLabsOrderedNotes('');
    setPatientHospitalized('');
    setPatientHospitalizedDate(undefined);
    setPatientHospitalizedTime('');
    setPatientHospitalizedNotes('');
    setCultureTaken('');
    setCultureTakenDate(undefined);
    setCultureTakenTime('');
    setCultureSource('');
    setTreatmentNotes('');
    setFollowUpDueDate(undefined);
    setFollowUpAssigneeId('');
    setFollowUpNotes('');
  };

  // Pre-fill form when editing an existing log
  useEffect(() => {
    if (!open || !editingLogId) return;
    (async () => {
      try {
        // Use cached data if provided (avoids double-fetch from detail dialog)
        let d = editingLogData ?? null;
        if (!d) {
          const res = await getInfectionLogDetail({
            path: { patient_id: patientId, infection_log_id: editingLogId },
          });
          d = res.data ?? null;
        }
        if (!d) return;
        setPrimaryPhysicianId(d.primary_physician_id ?? '');
        setCaseManagerId(d.case_manager_id ?? '');
        setReportedByUserId(d.reported_by_user_id ?? '');
        setReportedToUserId(d.reported_to_user_id ?? '');
        setDateReported(
          d.date_reported ? parseISOToLocalDate(d.date_reported) : undefined
        );
        setTimeReported(normalizeTimeValue(d.time_reported ?? '') || '');
        setInfectionStatus(d.infection_status ?? 'investigating');
        setDateOfOnset(
          d.date_of_onset ? parseISOToLocalDate(d.date_of_onset) : undefined
        );
        setPresentUponAdmission(d.present_upon_admission ?? false);
        setInfectionType(d.infection_type ?? '');
        setInfectionTypeOther(d.infection_type_other ?? '');
        setInfectionSite(d.infection_site ?? '');
        setInfectionSiteOther(d.infection_site_other ?? '');
        setDiscoveryLocation(d.discovery_location ?? '');
        setDiscoveryLocationOther(d.discovery_location_other ?? '');
        setIsCommunicable(
          d.is_communicable === true
            ? 'yes'
            : d.is_communicable === false
              ? 'no'
              : ''
        );
        setInfectionDescription(d.infection_description ?? '');
        setSignsSymptomsText(d.signs_symptoms ?? '');
        setPhysicianNotified(d.physician_notified ?? '');
        setPhysicianNotifiedPhysicianId(
          d.physician_notified_physician_id ?? ''
        );
        setPhysicianNotifiedDate(
          d.physician_notified_date
            ? parseISOToLocalDate(d.physician_notified_date)
            : undefined
        );
        setPhysicianNotifiedTime(
          normalizeTimeValue(d.physician_notified_time ?? '') || ''
        );
        setLabsOrdered(d.labs_ordered ?? '');
        setLabsOrderedDate(
          d.labs_ordered_date
            ? parseISOToLocalDate(d.labs_ordered_date)
            : undefined
        );
        setLabsOrderedTime(normalizeTimeValue(d.labs_ordered_time ?? '') || '');
        setLabsOrderedNotes(d.labs_ordered_notes ?? '');
        setPatientHospitalized(d.patient_hospitalized ?? '');
        setPatientHospitalizedDate(
          d.patient_hospitalized_date
            ? parseISOToLocalDate(d.patient_hospitalized_date)
            : undefined
        );
        setPatientHospitalizedTime(
          normalizeTimeValue(d.patient_hospitalized_time ?? '') || ''
        );
        setPatientHospitalizedNotes(d.patient_hospitalized_notes ?? '');
        setCultureTaken(d.culture_taken ?? '');
        setCultureTakenDate(
          d.culture_taken_date
            ? parseISOToLocalDate(d.culture_taken_date)
            : undefined
        );
        setCultureTakenTime(
          normalizeTimeValue(d.culture_taken_time ?? '') || ''
        );
        setCultureSource(d.culture_source ?? '');
        setTreatmentNotes(d.treatment_notes ?? '');
        setFollowUpDueDate(
          d.follow_up_due_date
            ? parseISOToLocalDate(d.follow_up_due_date)
            : undefined
        );
        setFollowUpAssigneeId(d.follow_up_assignee_id ?? '');
        // Pre-populate COVID symptoms from most recent screening
        const screenings = d.covid_screenings ?? [];
        const latestScreening = screenings[screenings.length - 1];
        if (latestScreening) {
          setCovidSymptoms({
            cough: latestScreening.symptom_cough ?? null,
            congestion: latestScreening.symptom_congestion ?? null,
            shortness_of_breath:
              latestScreening.symptom_shortness_of_breath ?? null,
            headache: latestScreening.symptom_headache ?? null,
            fever_chills: latestScreening.symptom_fever_chills ?? null,
            loss_taste_smell: latestScreening.symptom_loss_taste_smell ?? null,
            sore_throat: latestScreening.symptom_sore_throat ?? null,
            fatigue: latestScreening.symptom_fatigue ?? null,
            diarrhea: latestScreening.symptom_diarrhea ?? null,
            muscle_aches: latestScreening.symptom_muscle_aches ?? null,
            nausea_vomiting: latestScreening.symptom_nausea_vomiting ?? null,
            new_confusion: latestScreening.symptom_new_confusion ?? null,
          });
          setCovidContact(latestScreening.covid_contact ?? '');
        }
      } catch (err) {
        console.error('Failed to load infection log:', err);
      }
    })();
  }, [open, editingLogId, editingLogData]);

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const handleSubmit = async () => {
    if (!dateReported) {
      showError('Validation', 'Date Reported is required.');
      return;
    }
    if (!infectionType) {
      showError('Validation', 'Infection Type is required.');
      return;
    }

    setSubmitting(true);
    try {
      let covidScreening: InfectionCovidScreeningCreate | undefined;
      if (isCovid) {
        covidScreening = {
          screened_at: toISO(dateReported) ?? '',
          symptom_cough: covidSymptoms['cough'] ?? null,
          symptom_congestion: covidSymptoms['congestion'] ?? null,
          symptom_shortness_of_breath:
            covidSymptoms['shortness_of_breath'] ?? null,
          symptom_headache: covidSymptoms['headache'] ?? null,
          symptom_fever_chills: covidSymptoms['fever_chills'] ?? null,
          symptom_loss_taste_smell: covidSymptoms['loss_taste_smell'] ?? null,
          symptom_sore_throat: covidSymptoms['sore_throat'] ?? null,
          symptom_fatigue: covidSymptoms['fatigue'] ?? null,
          symptom_diarrhea: covidSymptoms['diarrhea'] ?? null,
          symptom_muscle_aches: covidSymptoms['muscle_aches'] ?? null,
          symptom_nausea_vomiting: covidSymptoms['nausea_vomiting'] ?? null,
          symptom_new_confusion: covidSymptoms['new_confusion'] ?? null,
          covid_contact: covidContact || null,
        };
      }

      const sharedBody = {
        date_reported: toISO(dateReported) ?? '',
        time_reported: timeReported || null,
        primary_physician_id: primaryPhysicianId || null,
        case_manager_id: caseManagerId || null,
        reported_by_user_id: reportedByUserId || null,
        reported_to_user_id: reportedToUserId || null,
        infection_status: infectionStatus as
          | 'investigating'
          | 'probable'
          | 'confirmed',
        date_of_onset: presentUponAdmission ? null : toISO(dateOfOnset),
        present_upon_admission: presentUponAdmission,
        infection_site: (infectionSite as never) || null,
        infection_site_other:
          infectionSite === 'other' ? infectionSiteOther || null : null,
        discovery_location: (discoveryLocation as never) || null,
        discovery_location_other:
          discoveryLocation === 'other' ? discoveryLocationOther || null : null,
        is_communicable:
          isCommunicable === 'yes'
            ? true
            : isCommunicable === 'no'
              ? false
              : null,
        infection_description: isCovid ? null : infectionDescription || null,
        signs_symptoms: isCovid ? null : signsSymptomsText || null,
        physician_notified: physicianNotified || null,
        physician_notified_physician_id:
          physicianNotified === 'yes'
            ? physicianNotifiedPhysicianId || null
            : null,
        physician_notified_date:
          physicianNotified === 'yes' ? toISO(physicianNotifiedDate) : null,
        physician_notified_time:
          physicianNotified === 'yes' ? physicianNotifiedTime || null : null,
        labs_ordered: labsOrdered || null,
        labs_ordered_date:
          labsOrdered === 'yes' ? toISO(labsOrderedDate) : null,
        labs_ordered_time:
          labsOrdered === 'yes' ? labsOrderedTime || null : null,
        labs_ordered_notes:
          labsOrdered === 'yes' ? labsOrderedNotes || null : null,
        patient_hospitalized: patientHospitalized || null,
        patient_hospitalized_date:
          patientHospitalized === 'yes' ? toISO(patientHospitalizedDate) : null,
        patient_hospitalized_time:
          patientHospitalized === 'yes'
            ? patientHospitalizedTime || null
            : null,
        patient_hospitalized_notes:
          patientHospitalized === 'yes'
            ? patientHospitalizedNotes || null
            : null,
        culture_taken: cultureTaken || null,
        culture_taken_date:
          cultureTaken === 'yes' ? toISO(cultureTakenDate) : null,
        culture_taken_time:
          cultureTaken === 'yes' ? cultureTakenTime || null : null,
        culture_source: cultureTaken === 'yes' ? cultureSource || null : null,
        treatment_notes: treatmentNotes || null,
        follow_up_due_date: toISO(followUpDueDate),
        follow_up_assignee_id: followUpAssigneeId || null,
      };

      let res;
      if (isEditing && editingLogId) {
        res = await updateInfectionLog({
          path: {
            patient_id: patientId,
            infection_log_id: editingLogId,
          },
          body: sharedBody,
        });
      } else {
        res = await createInfectionLog({
          path: { patient_id: patientId },
          body: {
            ...sharedBody,
            // Stamp the selected episode only on create — edits keep the
            // log's own episode (the backend leaves absent fields untouched).
            episode_id: episodeId ?? null,
            infection_type: infectionType as never,
            infection_type_other:
              infectionType === 'other' ? infectionTypeOther || null : null,
            covid_screening: covidScreening,
          },
        });
      }

      if (res.error) {
        console.error('Failed to save infection log:', res.error);
        // Surface actionable validation messages (e.g. date outside the
        // episode) — the date pickers can't catch every case, such as
        // timezone differences with the server's business date.
        const { message } = parse4xxHeyApiResult(res);
        showError(
          'Save Failed',
          message ?? 'Failed to save infection log. Please try again.'
        );
        return;
      }

      showSuccess('Infection Log Saved', 'Infection log saved.');
      resetForm();
      onCreated();
    } catch (err) {
      console.error('Error creating infection log:', err);
      showError('Error', 'Failed to save infection log. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={o => {
        if (!o) handleClose();
      }}
    >
      <DialogContent className='w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90vh] overflow-y-auto'>
        <DialogHeader>
          <DialogTitle>
            {isEditing ? 'Edit Infection Log' : 'Log New Infection'}
          </DialogTitle>
          <DialogDescription className='sr-only'>
            Add a new infection log for this patient
          </DialogDescription>
        </DialogHeader>

        <div className='space-y-4'>
          {/* ── Patient Information ─────────────────────────────── */}
          <div className='border rounded-lg px-4 py-3 space-y-3'>
            <p className='text-xs font-semibold uppercase tracking-wide text-muted-foreground'>
              Patient Information
            </p>

            <div className='grid grid-cols-1 sm:grid-cols-2 gap-3'>
              {/* Row 1: Primary Physician | Case Manager */}
              <div>
                <FieldLabel>Primary Physician</FieldLabel>
                <Select
                  value={primaryPhysicianId}
                  onValueChange={setPrimaryPhysicianId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select' />
                  </SelectTrigger>
                  <SelectContent>
                    {physicians.map(p => (
                      <SelectItem key={p.id} value={p.id}>
                        {getPhysicianLabel(p)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <FieldLabel>Case Manager</FieldLabel>
                <Select value={caseManagerId} onValueChange={setCaseManagerId}>
                  <SelectTrigger>
                    <SelectValue placeholder='Select' />
                  </SelectTrigger>
                  <SelectContent>
                    {clinicians.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {getClinicianDisplayName(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Row 2: Infection Reported By | Infection Reported To */}
              <div>
                <FieldLabel>Infection Reported By</FieldLabel>
                <Select
                  value={reportedByUserId}
                  onValueChange={setReportedByUserId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select' />
                  </SelectTrigger>
                  <SelectContent>
                    {clinicians.map(c => (
                      <SelectItem key={c.user_id} value={c.user_id}>
                        {getClinicianDisplayName(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <FieldLabel>Infection Reported To</FieldLabel>
                <Select
                  value={reportedToUserId}
                  onValueChange={setReportedToUserId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select' />
                  </SelectTrigger>
                  <SelectContent>
                    {clinicians.map(c => (
                      <SelectItem key={`to-${c.user_id}`} value={c.user_id}>
                        {getClinicianDisplayName(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Row 3: Date Reported | Time Reported */}
              <div>
                <FieldLabel required>Date Reported</FieldLabel>
                <DatePicker
                  date={dateReported}
                  onDateChange={setDateReported}
                  minDate={minReportedDate}
                  maxDate={maxReportedDate}
                />
              </div>

              <div>
                <FieldLabel>Time Reported</FieldLabel>
                <TimePicker
                  time={timeReported || undefined}
                  onTimeChange={time => setTimeReported(time ?? '')}
                />
              </div>
            </div>
          </div>

          {/* ── Infection Information ────────────────────────────── */}
          <CollapsibleSection title='Infection Information' defaultOpen>
            <div className='space-y-3'>
              {/* Status */}
              <div>
                <FieldLabel>Status</FieldLabel>
                <div className='flex gap-4'>
                  {['investigating', 'probable', 'confirmed'].map(s => (
                    <label
                      key={s}
                      className='flex items-center gap-1.5 cursor-pointer text-sm'
                    >
                      <input
                        type='radio'
                        name='infection_status'
                        value={s}
                        checked={infectionStatus === s}
                        onChange={() => setInfectionStatus(s)}
                        className='accent-primary'
                      />
                      {s.charAt(0).toUpperCase() + s.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Date of Onset */}
              {!presentUponAdmission && (
                <div>
                  <FieldLabel>Date of Onset</FieldLabel>
                  <DatePicker
                    date={dateOfOnset}
                    onDateChange={setDateOfOnset}
                    maxDate={today}
                  />
                </div>
              )}
              <label className='flex items-center gap-2 text-sm cursor-pointer'>
                <input
                  type='checkbox'
                  checked={presentUponAdmission}
                  onChange={e => {
                    setPresentUponAdmission(e.target.checked);
                    if (e.target.checked) setDateOfOnset(undefined);
                  }}
                  className='accent-primary'
                />
                Present Upon Admission
              </label>

              {/* Infection Type */}
              <div>
                <FieldLabel required>Infection Type</FieldLabel>
                <Select
                  value={infectionType}
                  onValueChange={setInfectionType}
                  disabled={isEditing}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select' />
                  </SelectTrigger>
                  <SelectContent>
                    {INFECTION_TYPE_OPTIONS.map(t => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {infectionType === 'other' && (
                  <input
                    type='text'
                    maxLength={25}
                    placeholder='Type description'
                    value={infectionTypeOther}
                    onChange={e => setInfectionTypeOther(e.target.value)}
                    className='mt-1.5 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                  />
                )}
              </div>

              {/* Infection Site */}
              <div>
                <FieldLabel>Infection Site</FieldLabel>
                <Select value={infectionSite} onValueChange={setInfectionSite}>
                  <SelectTrigger>
                    <SelectValue placeholder='Select' />
                  </SelectTrigger>
                  <SelectContent>
                    {INFECTION_SITES.map(s => (
                      <SelectItem key={s.value} value={s.value}>
                        {s.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {infectionSite === 'other' && (
                  <input
                    type='text'
                    maxLength={25}
                    placeholder='Site description'
                    value={infectionSiteOther}
                    onChange={e => setInfectionSiteOther(e.target.value)}
                    className='mt-1.5 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                  />
                )}
              </div>

              {/* Patient Location When Discovered */}
              <div>
                <FieldLabel>Patient Location When Discovered</FieldLabel>
                <Select
                  value={discoveryLocation}
                  onValueChange={setDiscoveryLocation}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select' />
                  </SelectTrigger>
                  <SelectContent>
                    {DISCOVERY_LOCATIONS.map(l => (
                      <SelectItem key={l.value} value={l.value}>
                        {l.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {discoveryLocation === 'other' && (
                  <input
                    type='text'
                    maxLength={25}
                    placeholder='Location description'
                    value={discoveryLocationOther}
                    onChange={e => setDiscoveryLocationOther(e.target.value)}
                    className='mt-1.5 flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                  />
                )}
              </div>

              {/* Communicable */}
              <div>
                <FieldLabel>Communicable?</FieldLabel>
                <div className='flex gap-4'>
                  {['yes', 'no'].map(v => (
                    <label
                      key={v}
                      className='flex items-center gap-1.5 cursor-pointer text-sm'
                    >
                      <input
                        type='radio'
                        name='communicable'
                        value={v}
                        checked={isCommunicable === v}
                        onChange={() => setIsCommunicable(v)}
                        className='accent-primary'
                      />
                      {v.charAt(0).toUpperCase() + v.slice(1)}
                    </label>
                  ))}
                </div>
              </div>

              {/* Description / Signs & Symptoms OR COVID-19 Screening */}
              {!isCovid ? (
                <>
                  <div>
                    <FieldLabel>Infection Description</FieldLabel>
                    <textarea
                      maxLength={2000}
                      rows={4}
                      value={infectionDescription}
                      onChange={e => setInfectionDescription(e.target.value)}
                      className='flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none'
                    />
                  </div>
                  <div>
                    <FieldLabel>Signs & Symptoms</FieldLabel>
                    <textarea
                      maxLength={2000}
                      rows={4}
                      value={signsSymptomsText}
                      onChange={e => setSignsSymptomsText(e.target.value)}
                      className='flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none'
                    />
                  </div>
                </>
              ) : (
                <div className='border rounded-md p-3 space-y-2 bg-muted/20'>
                  <p className='text-sm font-medium'>
                    Patient Symptom Screening for COVID-19
                  </p>
                  <div className='grid grid-cols-1 sm:grid-cols-2 gap-2'>
                    {COVID_SYMPTOMS.map(symptom => (
                      <div
                        key={symptom.key}
                        className='flex items-center justify-between text-sm'
                      >
                        <span>{symptom.label}</span>
                        <div className='flex gap-3'>
                          {['yes', 'no'].map(v => (
                            <label
                              key={v}
                              className='flex items-center gap-1 cursor-pointer'
                            >
                              <input
                                type='radio'
                                name={`covid_${symptom.key}`}
                                value={v}
                                checked={
                                  covidSymptoms[symptom.key] === (v === 'yes')
                                }
                                onChange={() =>
                                  setCovidSymptoms(prev => ({
                                    ...prev,
                                    [symptom.key]: v === 'yes',
                                  }))
                                }
                                className='accent-primary'
                              />
                              {v.charAt(0).toUpperCase() + v.slice(1)}
                            </label>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>
                  <div>
                    <FieldLabel>
                      Contact with confirmed/suspected COVID-19 case?
                    </FieldLabel>
                    <div className='flex gap-4'>
                      {['yes', 'no', 'uncertain'].map(v => (
                        <label
                          key={v}
                          className='flex items-center gap-1.5 cursor-pointer text-sm'
                        >
                          <input
                            type='radio'
                            name='covid_contact'
                            value={v}
                            checked={covidContact === v}
                            onChange={() => setCovidContact(v)}
                            className='accent-primary'
                          />
                          {v.charAt(0).toUpperCase() + v.slice(1)}
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              )}
            </div>
          </CollapsibleSection>

          {/* ── Actions Taken & Communication ───────────────────── */}
          <CollapsibleSection title='Actions Taken & Communication' defaultOpen>
            <div className='space-y-4'>
              {/* Physician Notified */}
              <div className='space-y-2'>
                <YesNoNaGroup
                  label='Physician Notified?'
                  name='physician_notified'
                  value={physicianNotified}
                  onChange={setPhysicianNotified}
                />
                {physicianNotified === 'yes' && (
                  <div className='ml-6 space-y-2'>
                    <div>
                      <FieldLabel>Physician</FieldLabel>
                      <Select
                        value={physicianNotifiedPhysicianId}
                        onValueChange={setPhysicianNotifiedPhysicianId}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder='Select' />
                        </SelectTrigger>
                        <SelectContent>
                          {physicians.map(p => (
                            <SelectItem key={p.id} value={p.id}>
                              {getPhysicianLabel(p)}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                    <div className='grid grid-cols-2 gap-2'>
                      <div>
                        <FieldLabel>Date Notified</FieldLabel>
                        <DatePicker
                          date={physicianNotifiedDate}
                          onDateChange={setPhysicianNotifiedDate}
                        />
                      </div>
                      <div>
                        <FieldLabel>Time</FieldLabel>
                        <TimePicker
                          time={physicianNotifiedTime || undefined}
                          onTimeChange={time =>
                            setPhysicianNotifiedTime(time ?? '')
                          }
                        />
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Labs Ordered */}
              <div className='space-y-2'>
                <YesNoNaGroup
                  label='Labs Ordered?'
                  name='labs_ordered'
                  value={labsOrdered}
                  onChange={setLabsOrdered}
                />
                {labsOrdered === 'yes' && (
                  <div className='ml-6 grid grid-cols-1 sm:grid-cols-2 gap-2'>
                    <div>
                      <FieldLabel>Date Ordered</FieldLabel>
                      <DatePicker
                        date={labsOrderedDate}
                        onDateChange={setLabsOrderedDate}
                      />
                    </div>
                    <div>
                      <FieldLabel>Time</FieldLabel>
                      <TimePicker
                        time={labsOrderedTime || undefined}
                        onTimeChange={time => setLabsOrderedTime(time ?? '')}
                      />
                    </div>
                    <div className='sm:col-span-2'>
                      <FieldLabel required>Notes</FieldLabel>
                      <input
                        type='text'
                        maxLength={500}
                        value={labsOrderedNotes}
                        onChange={e => setLabsOrderedNotes(e.target.value)}
                        className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Patient Hospitalized */}
              <div className='space-y-2'>
                <YesNoNaGroup
                  label='Patient Hospitalized?'
                  name='patient_hospitalized'
                  value={patientHospitalized}
                  onChange={setPatientHospitalized}
                />
                {patientHospitalized === 'yes' && (
                  <div className='ml-6 grid grid-cols-1 sm:grid-cols-2 gap-2'>
                    <div>
                      <FieldLabel>Date</FieldLabel>
                      <DatePicker
                        date={patientHospitalizedDate}
                        onDateChange={setPatientHospitalizedDate}
                      />
                    </div>
                    <div>
                      <FieldLabel>Time</FieldLabel>
                      <TimePicker
                        time={patientHospitalizedTime || undefined}
                        onTimeChange={time =>
                          setPatientHospitalizedTime(time ?? '')
                        }
                      />
                    </div>
                    <div className='sm:col-span-2'>
                      <FieldLabel>Notes</FieldLabel>
                      <input
                        type='text'
                        maxLength={500}
                        value={patientHospitalizedNotes}
                        onChange={e =>
                          setPatientHospitalizedNotes(e.target.value)
                        }
                        className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Culture Taken */}
              <div className='space-y-2'>
                <YesNoNaGroup
                  label='Culture Taken?'
                  name='culture_taken'
                  value={cultureTaken}
                  onChange={setCultureTaken}
                />
                {cultureTaken === 'yes' && (
                  <div className='ml-6 grid grid-cols-1 sm:grid-cols-2 gap-2'>
                    <div>
                      <FieldLabel>Date</FieldLabel>
                      <DatePicker
                        date={cultureTakenDate}
                        onDateChange={setCultureTakenDate}
                      />
                    </div>
                    <div>
                      <FieldLabel>Time</FieldLabel>
                      <TimePicker
                        time={cultureTakenTime || undefined}
                        onTimeChange={time => setCultureTakenTime(time ?? '')}
                      />
                    </div>
                    <div className='sm:col-span-2'>
                      <FieldLabel required>Source</FieldLabel>
                      <input
                        type='text'
                        maxLength={500}
                        value={cultureSource}
                        onChange={e => setCultureSource(e.target.value)}
                        className='flex h-9 w-full rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring'
                      />
                    </div>
                  </div>
                )}
              </div>

              {/* Treatment Notes (always visible) */}
              <div>
                <FieldLabel>Treatment Notes</FieldLabel>
                <textarea
                  maxLength={2000}
                  rows={3}
                  value={treatmentNotes}
                  onChange={e => setTreatmentNotes(e.target.value)}
                  className='flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none'
                />
              </div>
            </div>
          </CollapsibleSection>

          {/* ── Medication Administration ────────────────────────── */}
          <CollapsibleSection
            title='Medication Administration'
            defaultOpen={false}
          >
            <p className='text-sm text-muted-foreground'>
              Medication administration for this infection is managed on the{' '}
              <button
                type='button'
                onClick={handleClose}
                className='font-medium text-primary underline underline-offset-2 hover:text-primary/80'
              >
                Medications
              </button>{' '}
              tab.
            </p>
          </CollapsibleSection>

          {/* ── Follow-Up ────────────────────────────────────────── */}
          <CollapsibleSection title='Follow-Up' defaultOpen={false}>
            <div className='space-y-3'>
              <div>
                <FieldLabel>Follow-Up Due Date</FieldLabel>
                <DatePicker
                  date={followUpDueDate}
                  onDateChange={setFollowUpDueDate}
                />
              </div>
              <div>
                <FieldLabel>Follow-Up Assigned To</FieldLabel>
                <Select
                  value={followUpAssigneeId}
                  onValueChange={setFollowUpAssigneeId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder='Select' />
                  </SelectTrigger>
                  <SelectContent>
                    {clinicians.map(c => (
                      <SelectItem key={c.id} value={c.id}>
                        {getClinicianDisplayName(c)}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <FieldLabel>Follow-Up Notes</FieldLabel>
                <textarea
                  maxLength={2000}
                  rows={3}
                  value={followUpNotes}
                  onChange={e => setFollowUpNotes(e.target.value)}
                  className='flex w-full rounded-md border border-input bg-transparent px-3 py-2 text-sm shadow-sm placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring resize-none'
                />
              </div>
            </div>
          </CollapsibleSection>
        </div>

        <DialogFooter className='gap-2 mt-2'>
          <Button
            variant='outline'
            onClick={handleClose}
            disabled={submitting || deleting}
          >
            Cancel
          </Button>
          {isEditing && (
            <Button
              variant='destructive'
              onClick={() => setConfirmDeleteOpen(true)}
              disabled={submitting || deleting}
            >
              {deleting ? 'Deleting…' : 'Delete'}
            </Button>
          )}
          <Button
            onClick={handleSubmit}
            disabled={submitting || deleting || !isFormValid}
          >
            {submitting ? 'Saving…' : 'Save'}
          </Button>
        </DialogFooter>
        <ConfirmationDialog
          open={confirmDeleteOpen}
          title='Delete Infection Log'
          message='This will permanently delete this infection log and its follow-up history. This action cannot be undone.'
          confirmLabel='Delete'
          loading={deleting}
          onAccept={handleDelete}
          onCancel={() => setConfirmDeleteOpen(false)}
        />
      </DialogContent>
    </Dialog>
  );
};
